if (!customElements.get('wls-popup')) {
  class WLSPopup extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;

      this.classes = {
        open: 'wls-popup--open',
        overlay: 'wls-popup__overlay',
        container: 'wls-popup__container',
        close: 'wls-popup__close',
        content: 'wls-popup__content',
        contentInner: 'wls-popup__content-inner',
        fullHeight: 'wls-popup__content--full-height',
      };

      // Bind methods
      this.boundHandleTrigger = this.open.bind(this);
      this.boundHandleClose = this.close.bind(this);
      this.boundHandleEscape = this._handleEscape.bind(this);
    }

    /**
     * Get DOM elements
     * @returns {object} - Object containing DOM element references
     */
    get elements() {
      return {
        trigger: this.querySelector('[slot="trigger"]'),
        content: this.querySelector('[slot="content"]'),
        portal: this._getPortal(),
        portalContent: this._getPortal()?.querySelector(`.${this.classes.contentInner}`),
        overlay: this._getPortal()?.querySelector(`.${this.classes.overlay}`),
        closeButton: this._getPortal()?.querySelector(`.${this.classes.close}`),
      };
    }

    /**
     * Get the global popup portal from body (shared instance)
     * @returns {HTMLElement|null}
     */
    _getPortal() {
      return document.body.querySelector(`.${this.classes.container}`);
    }

    /**
     * Connected callback lifecycle method
     * @returns {void}
     */
    connectedCallback() {
      this._init();
      document.addEventListener('keydown', this.boundHandleEscape);
    }

    /**
     * Disconnected callback lifecycle method
     * @returns {void}
     */
    disconnectedCallback() {
      document.removeEventListener('keydown', this.boundHandleEscape);
      this._destroy();
    }

    /**
     * Initialize the popup
     * @returns {void}
     */
    _init() {
      if (this.initialized) return;
      this.initialized = true;

      const { trigger, content } = this.elements;

      if (!trigger || !content) {
        console.warn('wls-popup: missing trigger or content');
        return;
      }

      // Find global popup structure in body
      const portal = this._getPortal();

      if (!portal) {
        console.warn('wls-popup: global popup structure not found in body. Make sure to include wls-popup snippet at the bottom of body in theme.liquid.');
        return;
      }

      try {
        this._bindEvents();
      } catch (error) {
        console.error('Error initializing popup:', error);
      }
    }

    /**
     * Bind event listeners
     * @returns {void}
     */
    _bindEvents() {
      const { trigger, overlay, closeButton } = this.elements;

      if (!trigger) {
        console.warn('wls-popup: missing trigger element');
        return;
      }

      if (!overlay || !closeButton) {
        console.warn('wls-popup: global popup structure not found in body');
        return;
      }

      trigger.addEventListener('click', this.boundHandleTrigger);
      overlay.addEventListener('click', this.boundHandleClose);
      closeButton.addEventListener('click', this.boundHandleClose);
    }

    /**
     * Open the popup
     * @returns {void}
     */
    open() {
      if (this.isOpen) return;

      const portal = this._getPortal();
      if (!portal) {
        console.warn('wls-popup: global popup portal not found');
        return;
      }

      const { content } = this.elements;
      const contentInner = portal.querySelector(`.${this.classes.contentInner}`);
      const popupContent = portal.querySelector(`.${this.classes.content}`);

      if (!contentInner) {
        console.error('wls-popup: content inner element not found');
        return;
      }

      // Clear previous content
      contentInner.innerHTML = '';

      // Clone and append new content
      if (content) {
        const clonedContent = content.cloneNode(true);
        // Remove slot attribute from cloned content
        clonedContent.removeAttribute('slot');
        contentInner.appendChild(clonedContent);
      }

      // Apply full-height class based on full-height attribute
      if (popupContent) {
        const isFullHeight = this.getAttribute('full-height') === 'true';
        if (isFullHeight) {
          popupContent.classList.add(this.classes.fullHeight);
        } else {
          popupContent.classList.remove(this.classes.fullHeight);
        }
      }

      this.isOpen = true;
      portal.classList.add(this.classes.open);
      document.body.style.overflow = 'hidden';
    }

    /**
     * Close the popup
     * @returns {void}
     */
    close() {
      if (!this.isOpen) return;

      const portal = this._getPortal();
      if (!portal) {
        console.warn('wls-popup: global popup portal not found');
        return;
      }

      const popupContent = portal.querySelector(`.${this.classes.content}`);
      
      // Remove full-height class when closing
      if (popupContent) {
        popupContent.classList.remove(this.classes.fullHeight);
      }

      this.isOpen = false;
      portal.classList.remove(this.classes.open);
      document.body.style.overflow = '';

      // Clear content when closed
      const contentInner = portal.querySelector(`.${this.classes.contentInner}`);
      if (contentInner) {
        contentInner.innerHTML = '';
      }
    }

    /**
     * Handle escape key press
     * @param {KeyboardEvent} e - Keyboard event
     * @returns {void}
     */
    _handleEscape(e) {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    }

    /**
     * Destroy the popup instance properly
     * @returns {void}
     */
    _destroy() {
      try {
        // Remove event listeners
        const { trigger, overlay, closeButton } = this.elements;

        if (trigger) {
          trigger.removeEventListener('click', this.boundHandleTrigger);
        }

        if (overlay) {
          overlay.removeEventListener('click', this.boundHandleClose);
        }

        if (closeButton) {
          closeButton.removeEventListener('click', this.boundHandleClose);
        }

        // Reset state
        this.isOpen = false;
        this.initialized = false;
      } catch (error) {
        console.error('Error destroying popup instance:', error);
      }
    }
  }

  customElements.define('wls-popup', WLSPopup);
}